import { useEffect, useState } from "react";
import { PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/outline";
import {
  getPixels,
  deletePixels,
  createPixels,
  updatePixels,
} from "../../api/pixelsApi";
import PageLoader from "../../components/pageLoader";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import { useNavigate } from "react-router-dom";
import DeleteConfirmationModal from '../../components/deleteModal';
import PixelFormModal from './pixelsFormModal';

const PixelsPage = () => {
  const [showAddModal, setShowAddModal] = useState(false);

  const LIMIT = 10;

  const [pixels, setPixels] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deletePixelId, setDeletePixelId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / LIMIT);

  const [editingPixel, setEditingPixel] = useState(null);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null);

  const navigate = useNavigate();

  const getPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const fetchPixels = async () => {
    try {
      setLoading(true);
      setError("");

      const offset = (page - 1) * LIMIT;
      const data = await getPixels(LIMIT, offset);

      setPixels(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setError("Failed to load pixels.");
    } finally {
      setLoading(false);
    }
  };

  const handlePixelSubmit = async (info) => {
    try {
      setSaving(true);

      const payload = {
        name: info.name,
        pixel_type: info.type,
        pixel_id: info.pixel_id,
        // is_future_use: info.is_future_use,
      };

      if (editingPixel) {
        await updatePixels(editingPixel.id, payload);
      } else {
        await createPixels(payload);
      }

      setEditingPixel(null);
      setShowAddModal(false);

      fetchPixels();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePixel = async () => {
    try {
      setDeleting(true);
      await deletePixels(deletePixelId);

      setDeletePixelId(null);

      fetchPixels();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchPixels();
  }, [page]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Pixels</h1>
          <p className="text-sm text-slate-400">Manage pixels info.</p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          <PlusIcon className="w-4 h-4" />
          Create Pixel
        </button>
      </div>

      {error && <div className="p-6 text-red-400">{error}</div>}

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-center text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Pixel Id</th>
                {/* <th className="px-6 py-4">Is Future Use</th> */}
                <th className="px-6 py-4">Created at</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {pixels.map((px) => (
                <tr
                  key={px.id}
                  className="border-b border-white/5 last:border-none text-center hover:bg-slate-800/40 transition"
                >

                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-300">
                      {px.name}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {px.type}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {px.pixel_id}
                  </td>

                  {/* <td className="px-6 py-4 text-sm text-slate-300">
                    {px.is_future_use ? "Yes" : "No"}
                  </td> */}

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {px.created_at}
                  </td>

                  <td className="px-6 py-4 text-center">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingPixel(px)}
                        className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => setDeletePixelId(px.id)}
                        className="p-2 rounded-md text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition"
                        title="Delete pixel"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty state placeholder (future) */}
        {pixels.length === 0 && (
          <div className="p-8 text-center text-slate-400">No pixels found.</div>
        )}
      </div>

      <div className="flex justify-end">
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 text-sm text-slate-400">
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
                {/* Previous */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
            ${
              page === pageNumber
                ? "bg-pink-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                {/* Next */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <PixelFormModal
        isOpen={showAddModal || !!editingPixel}
        onClose={() => {
          setShowAddModal(false);
          setEditingPixel(null);
        }}
        onSubmit={handlePixelSubmit}
        loading={saving}
        mode={editingPixel ? "edit" : "create"}
        initialData={editingPixel}
      />

      <DeleteConfirmationModal
        isOpen={!!deletePixelId}
        description="Are you sure you want to delete this pixel?  This action cannot be undone."
        onClose={() => setDeletePixelId(null)}
        onConfirm={handleDeletePixel}
        loading={deleting}
      />

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default PixelsPage;
